import { Alert } from '@design-system';
import { useTranslation } from 'next-i18next';

import { useHubspotChat } from '../../../hooks/useHubspotChat';
import type {
  ProductPurchaseDetails,
  ProductPurchasingController,
} from '../../../types/Purchasing';
import { FramePicker } from '../../Containers/ReviewCheckoutDrawer/FramePicker';
import Drawer from '../../Layout/Drawer';

interface Props<T extends ProductPurchaseDetails> {
  product: ProductType;
  shown: boolean;
  purchasingController: ProductPurchasingController<T>;
  onClose: () => void;
  onSubmit?: () => void;
}

export const ProductFramingDrawer = <T extends ProductPurchaseDetails>({
  onClose,
  onSubmit,
  product,
  purchasingController,
  shown,
}: Props<T>) => {
  const { t } = useTranslation(['product']);
  const { openHandler: openHubspotChat } = useHubspotChat();

  if (
    !purchasingController ||
    purchasingController.product === null ||
    purchasingController.frameOptions?.length === 0
  ) {
    return null;
  }

  if (!shown) {
    return <Drawer onClose={onClose} shown={shown} />;
  }

  return (
    <Drawer onClose={onClose} shown={shown}>
      <div className="mx-auto w-full max-w-md">
        {purchasingController.frameOptions?.length ? (
          <FramePicker
            description={
              product.details.length ? product.details[0].framingOptions : ''
            }
            frameByDefault={purchasingController.isFramedByDefault}
            frameOptoins={purchasingController.frameOptions}
            frameSelected={purchasingController.frame}
            hideSubmitButton={false}
            hideTitle={false}
            includeUnframed
            onGetInTouchClick={() => {
              onClose();
              openHubspotChat();
            }}
            onSubmit={(frame) => {
              purchasingController.setFrame(frame);
              onSubmit?.();
              onClose();
            }}
            submitButtonLabel={t('drawers.review.framing.cta')}
          />
        ) : (
          <div className="flex flex-col gap-9">
            <div className="mb-6 hidden w-full flex-1 md:flex">
              <p className="text-md font-bold md:text-lg">
                {t('drawers.review.framing.title')}
              </p>
            </div>
            <Alert status="error">
              No frames found for the selected works.
            </Alert>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default ProductFramingDrawer;
